import { lazy, Suspense } from "react";
import Loading from "../components/commonControls/loading";
import {
  signUpRoute,
  loginRoute,
  pushNotificationRoute,
  createUserRoute,
  modifyRolesRoute,
  usersRoute,
  rolesRoute,
  projectsRoute,
  createProjectRoute,
  userGroupsRoute,
  createGroupRoute,
  groupAddRoute,
  editProfile,
  changePassword,
  memberCardsRoute,
  loyalMembersRoute,
  createCardRoute,
  membershipTiersRoute,
  updateTiersRoute,
  projectAddRoute,
  createMembersBulk,
  offerCatalogRoute,
  createOfferRoute,
  retailorVendorRoute,
  dashboardRoute,
  createVendorRoute,
  vendorBulkUpdateRoute,
  createEventRoute,
  eventsRoute,
  forgotPasswordRoute,
  OTPVerifyRoute,
  resetPasswordRoute,
  memberReportRoute,
  offerReportRoute,
  memberTransactionsReportRoute,
  offerTransactionsReportRoute,
  inviteMembersRoute,
  memberDetailsRoute,
  cardPrintJobsRoute,
  createNotificationsRoute,
  viewTierMembers,
  bulkUpdateTiersRoute,
  moderationRoute,
  clientRoute,
  createClient,
  moderationHistoryRoute,
  selectPlanRoute,
  checkoutFormRoute,
  myPlanRoute,
  myPaymentsRout,
  plansRoute,
  cmsRoute,
} from "./reactRoutes";
//Import components for routing that don't need to wait for data from api before loading
import Login from "../components/authentication/Login";
import RegisterSimple from "../components/authentication/RegisterSimple";
import ForgetPassword from "../components/authentication/ForgetPassword";
import OTPVerify from "../components/authentication/OTPVerify";
import ResetPassword from "../components/authentication/ResetPassword";

//title suffix
const suffix = " - City Center";

// Lazy Import other components for routes
const Push = lazy(() => import("../components/pushMessages/Push"));
const Notifications = lazy(() =>
  import("../components/pushMessages/Notifications")
);
const Clients = lazy(() => import("../components/clients/Clients"));
const RoleEdit = lazy(() => import("../components/usersRoles/RoleEdit"));
const UserCreate = lazy(() => import("../components/usersRoles/UserCreate"));
const RoleManagement = lazy(() =>
  import("../components/usersRoles/RoleManagement")
);
const UserManagement = lazy(() =>
  import("../components/usersRoles/UserManagement")
);
const Projects = lazy(() => import("../components/projects/Projects"));
const ProjectCreate = lazy(() =>
  import("../components/projects/ProjectCreate")
);
const GroupManagement = lazy(() =>
  import("../components/groups/GroupManagement")
);
const CreateGroup = lazy(() => import("../components/groups/GroupCreate"));
const GroupAdd = lazy(() => import("../components/groups/GroupAdd"));
const EditProfile = lazy(() => import("../components/Profile/EditProfile"));
const ChangePassword = lazy(() =>
  import("../components/Profile/ChangePassword")
);
const MembershipCards = lazy(() =>
  import("../components/loyaltyMembers/MembershipCards")
);
const MembershipTiers = lazy(() =>
  import("../components/loyaltyMembers/MembershipTiers")
);
const LoyaltyMembers = lazy(() =>
  import("../components/loyaltyMembers/LoyaltyMembers")
);
const CreateCard = lazy(() =>
  import("../components/loyaltyMembers/CreateCard")
);
const TiersAdd = lazy(() => import("../components/loyaltyMembers/UpdateTiers"));
const TierMembers = lazy(() =>
  import("../components/loyaltyMembers/TierMembers")
);
const ProjectAdd = lazy(() => import("../components/projects/ProjectAdd"));
const CreateCSV = lazy(() => import("../components/loyaltyMembers/CreateCSV"));
const CreateOffer = lazy(() => import("../components/offers/CreateOffer"));
const OffersManagement = lazy(() =>
  import("../components/offers/OffersManagement")
);
const VendorManagement = lazy(() =>
  import("../components/vendor/VendorManagement")
);
const MainPage = lazy(() => import("../components/dashboard/MainPage"));
const RegisterVendor = lazy(() =>
  import("../components/vendor/RegisterVendor")
);
const BulkUpdate = lazy(() => import("../components/vendor/BulkUpdate"));
const EventManagement = lazy(() =>
  import("../components/events/EventManagement")
);
const CreateEvent = lazy(() => import("../components/events/CreateEvent"));
const MemberReport = lazy(() => import("../components/reporting/MemberReport"));
const OfferReport = lazy(() => import("../components/reporting/OfferReport"));
const MemberTransactions = lazy(() =>
  import("../components/reporting/MemberTransactions")
);
const OfferTransactions = lazy(() =>
  import("../components/reporting/OfferTransactions")
);
const MemberCreate = lazy(() =>
  import("../components/loyaltyMembers/MemberCreate")
);
const MemberDetails = lazy(() =>
  import("../components/loyaltyMembers/MemberDetails")
);
const BulkUpdateTiers = lazy(() =>
  import("../components/loyaltyMembers/BulkUpdateTiers")
);
const PrintJobs = lazy(() => import("../components/loyaltyMembers/PrintJobs"));

const Requests = lazy(() => import("../components/moderation/Requests"));
const ModHistory = lazy(() => import("../components/moderation/History"));
const CreateClient = lazy(() => import("../components/clients/ClientCreate"));
const SubscriptionPlans = lazy(() =>
  import("../components/subscriptions/plans")
);
const CheckoutForm = lazy(() => import("../components/subscriptions/checkout"));
const MyPlan = lazy(() => import("../components/subscriptions/payments"));
const MyPayments = lazy(() =>
  import("../components/subscriptions/allPayments")
);
const Plans = lazy(() => import("../components/clients/Plans"));
const CMSHomePage = lazy(() => import("../components/cms/CMSHomePage"));

// List of all private Routes with corresponding components
export const PrivateRoutes = [
  {
    path: editProfile,
    element: (
      <Suspense fallback={<Loading />}>
        <EditProfile title={`Update Profile${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: changePassword,
    element: (
      <Suspense fallback={<Loading />}>
        <ChangePassword title={`Change Password${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: usersRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <UserManagement title={`Users${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: rolesRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <RoleManagement title={`Roles${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: modifyRolesRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <RoleEdit title={`Update Roles${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: createUserRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <UserCreate title={`Register User${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: clientRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <Clients title={`Clients${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: createClient,
    element: (
      <Suspense fallback={<Loading />}>
        <CreateClient title={`Invite Client${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: createNotificationsRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <Push title={`Create Notifications${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: pushNotificationRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <Notifications title={`Notifications${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: userGroupsRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <GroupManagement title={`Groups${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: createGroupRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <CreateGroup title={`Update Groups${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: groupAddRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <GroupAdd title={`Update Group Members${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: projectsRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <Projects title={`Projects${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: createProjectRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <ProjectCreate title={`Update Projects${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: projectAddRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <ProjectAdd title={`Update Project Members${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: memberCardsRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <MembershipCards title={`Cards${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: loyalMembersRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <LoyaltyMembers title={`Loyalty Members${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: bulkUpdateTiersRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <BulkUpdateTiers title={`Bulk Update Tiers${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: createCardRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <CreateCard title={`Update Cards${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: membershipTiersRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <MembershipTiers title={`Tiers${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: updateTiersRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <TiersAdd title={`Update Tier Members${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: viewTierMembers,
    element: (
      <Suspense fallback={<Loading />}>
        <TierMembers title={`View Tier Members${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: createMembersBulk,
    element: (
      <Suspense fallback={<Loading />}>
        <CreateCSV title={`Members Bulk Update${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: offerCatalogRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <OffersManagement title={`Offers${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: createOfferRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <CreateOffer title={`Update Offers${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: retailorVendorRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <VendorManagement title={`Vendors${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: dashboardRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <MainPage title={`Home${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: createVendorRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <RegisterVendor title={`Register Vendor${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: vendorBulkUpdateRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <BulkUpdate title={`Vendors Bulk Update${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: eventsRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <EventManagement title={`Events${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: createEventRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <CreateEvent title={`Update Events${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: memberReportRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <MemberReport title={`Loyalty Members Report${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: offerReportRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <OfferReport title={`Offers Redemption Report${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: memberTransactionsReportRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <MemberTransactions title={`Member Transactions Report${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: offerTransactionsReportRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <OfferTransactions title={`Offer Transactions Report${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: inviteMembersRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <MemberCreate title={`Invite New Member${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: memberDetailsRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <MemberDetails title={`Member Details${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: cardPrintJobsRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <PrintJobs title={`Card Printing Jobs${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: moderationRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <Requests title={`Moderation Requests${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: moderationHistoryRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <ModHistory title={`Moderation History${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: selectPlanRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <SubscriptionPlans title={`Select Plan${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: checkoutFormRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <CheckoutForm title={`Payment Details${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: myPlanRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <MyPlan title={`My Plan${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: myPaymentsRout,
    element: (
      <Suspense fallback={<Loading />}>
        <MyPayments title={`My Payments${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: plansRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <Plans title={`Plans${suffix}`} />
      </Suspense>
    ),
  },
  {
    path: cmsRoute,
    element: (
      <Suspense fallback={<Loading />}>
        <CMSHomePage title={`CMS${suffix}`} />
      </Suspense>
    ),
  },
];

// List of all public routes with corresponding components
export const PublicRoutes = [
  { path: loginRoute, element: <Login title={`Login${suffix}`} /> },
  {
    path: signUpRoute,
    element: <RegisterSimple title={`Register${suffix}`} />,
  },
  {
    path: forgotPasswordRoute,
    element: <ForgetPassword title={`Password Reset${suffix}`} />,
  },
  {
    path: OTPVerifyRoute,
    element: <OTPVerify title={`Verify OTP${suffix}`} />,
  },
  {
    path: resetPasswordRoute,
    element: <ResetPassword title={`Reset Password${suffix}`} />,
  },
];
